import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const isSpeechSupported =
  typeof window !== "undefined" &&
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);

export const isMediaRecorderSupported =
  typeof window !== "undefined" && !!window.MediaRecorder;

/**
 * useSpeechToText
 *
 * Encapsulates Web Speech API + MediaRecorder logic.
 *
 * @param {object}   options
 * @param {string}   [options.initialLang="ko-KR"]  BCP-47 language code
 * @param {function} [options.onError]               Called with an error message string
 * @param {function} [options.onWarning]             Called with a warning message string
 */
export function useSpeechToText({ initialLang = "ko-KR", onError, onWarning } = {}) {
  const { t } = useTranslation();
  const [textInput, setTextInput] = useState("");
  const [interimText, setInterimText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [lang, setLang] = useState(initialLang);
  const [includeAudio, setIncludeAudio] = useState(true);
  const [status, setStatus] = useState("Ready");

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioBase64Ref = useRef(null);
  const streamRef = useRef(null);
  const isRecordingRef = useRef(false);

  // Keep ref in sync so async callbacks (onend) can read the latest value
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_) {}
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (_) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    isRecordingRef.current = false;
    setInterimText("");
    setStatus("Ready");
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSpeechSupported) {
      onWarning?.(t("teleop.speech.notSupported"));
      return;
    }

    audioBase64Ref.current = null;
    audioChunksRef.current = [];

    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (err) {
      onError?.(t("teleop.speech.micDenied", { message: err.message }));
      return;
    }

    if (includeAudio && isMediaRecorderSupported) {
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        try {
          audioBase64Ref.current = await blobToBase64(blob);
        } catch (_) {
          audioBase64Ref.current = null;
        }
      };
      recorder.start();
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setTextInput((prev) =>
          prev ? prev + " " + finalTranscript.trim() : finalTranscript.trim()
        );
      }
      setInterimText(interimTranscript);
    };

    recognition.onerror = (event) => {
      if (event.error !== "aborted") {
        onError?.(t("teleop.speech.recognitionError", { error: event.error }));
      }
      stopRecording();
    };

    recognition.onend = () => {
      // Auto-restart if still recording (continuous mode can end unexpectedly)
      if (isRecordingRef.current && recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (_) {}
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err) {
      onError?.(t("teleop.speech.startFailed", { message: err.message }));
      stopRecording();
      return;
    }

    setIsRecording(true);
    isRecordingRef.current = true;
    setStatus("Recording...");
  }, [lang, includeAudio, stopRecording, onError, onWarning, t]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const resetTranscript = useCallback(() => {
    setTextInput("");
    setInterimText("");
    audioBase64Ref.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, []);

  // Stop when language changes mid-recording
  useEffect(() => {
    if (isRecording) {
      stopRecording();
    }
  }, [lang]);

  return {
    textInput,
    setTextInput,
    interimText,
    isRecording,
    lang,
    setLang,
    includeAudio,
    setIncludeAudio,
    status,
    setStatus,
    audioBase64: audioBase64Ref.current,
    audioBase64Ref,
    isSpeechSupported,
    isMediaRecorderSupported,
    startRecording,
    stopRecording,
    toggleRecording,
    resetTranscript,
  };
}
